import {
  EngagementOptionEnum,
  EngagementOptionEnumValues,
  EngagementTypeEnum,
  EngagementTypeEnumValues,
  MarketMakerEngagementsStatusEnumValues,
  MarketMakerKpiTypeEnumValues,
  MarketMakerKpiTypeEnum,
  MarketMakerQuoteLoanCustodyEnumValues,
  MarketMakerQuoteTranchePriceMethodologyEnumValues,
  OptionsStyleEnumValues,
} from '@forgd/shared/enums'
import type { MarketMakerKpiTypeEnumType } from '@forgd/shared/enums'
import { UTCDate } from '@date-fns/utc'
import { addHours, isAfter } from 'date-fns'
import { formatValue } from '@forgd/formatter'
import { isFloatGreaterWithPrecision, isFloatLessWithPrecision } from '@forgd/shared/utils/numbers'
import { z } from 'zod'
import { KpiResultSchema } from '../kpi'

const FUTURE_DATE_GRACE_HOURS = 24

export const MmEngagementTrancheSchema = z.object({
  trancheNumber: z.number().int().min(1).max(4),
  tokenSupplyPercentage: z.number().safe().positive().finite(),
  priceMethodology: z.enum(MarketMakerQuoteTranchePriceMethodologyEnumValues),
  priceValue: z.number().safe().nonnegative().finite(),
  twapThresholdDays: z.number().safe().int().nullable(),
  timePeriod: z.number().safe().int().nonnegative().finite(),
})

export type MmEngagementTranche = z.infer<typeof MmEngagementTrancheSchema>

// Spot trading pair for exchange-type engagements. Pair 1's `baseCoingeckoId`
// is also the engagement's project — the FE mirrors it into the top-level
// `coingeckoId` so the controller's existing `resolveProjectId` path can
// upsert / find the matching `core.projects` row, exactly like Retainer's
// CoinSelect does.
export const MmEngagementTradingPairSchema = z.object({
  pairNumber: z.number().int().min(1),
  baseCoingeckoId: z.string().min(1),
  quoteCoingeckoId: z.string().min(1),
})

export type MmEngagementTradingPair = z.infer<typeof MmEngagementTradingPairSchema>

const EngagementSummarySchema = z.object({
  id: z.string().uuid(),
  status: z.enum(MarketMakerEngagementsStatusEnumValues),
  type: z.enum(EngagementTypeEnumValues),
  firstReportReceivedAt: z.string().date().nullable(),
  lastReportReceivedAt: z.string().date().nullable(),
  totalReportsCount: z.number().safe().nullable(),
  projectId: z.string().uuid(),
  projectName: z.string(),
  projectImage: z.string().nullable(),
  projectTicker: z.string().nullable(),
  exchangeId: z.string().nullable(),
  exchangeName: z.string().nullable(),
  exchangeImage: z.string().nullable(),
  tradingPairBaseTickers: z.array(z.string()),
})

export const GetMmEngagementsResponseBodySchema = z.array(EngagementSummarySchema)

export const GetAllMmEngagementsQueryParamsSchema = z.object({
  marketMakerId: z.string().optional(),
  projectId: z.string().uuid().optional(),
  statuses: z
    .preprocess(
      (val) => (typeof val === 'string' ? [val] : val),
      z.array(z.enum(MarketMakerEngagementsStatusEnumValues)),
    )
    .optional(),
})

export const AdminMmEngagementSchema = EngagementSummarySchema.extend({
  isVerified: z.boolean(),
  verificationLevel: z.number().int(),
  currentVerificationLevel: z.number().int(),
  hasPendingReview: z.boolean(),
  marketMakerId: z.string(),
  marketMakerName: z.string(),
  marketMakerImage: z.string().nullable(),
  groupId: z.number().int(),
  startDate: z.string().nullable(),
  latestUpdateAt: z.string().nullable(),
  endDate: z.string().nullable(),
})

export type AdminMmEngagement = z.infer<typeof AdminMmEngagementSchema>

export const GetAllMmEngagementsResponseBodySchema = z.array(AdminMmEngagementSchema)

// `BaseEngagementSchema` only backs the response shape now
// (FullEngagementResponseSchema), which overrides `type` to the wider
// engagement-type enum so Prop Trading / Exchange rows can be returned.
const BaseEngagementSchema = z.object({
  type: z.enum(EngagementOptionEnumValues),
  qtyTokensLoan: z.number().safe(),
  qtyTokensWorkingCapital: z.number().safe(),
  qtyStablesWorkingCapital: z.number().safe(),
  generalUptimePercentage: z.number().safe().nullable(),
  kpiOrderBookDominanceThreshold: z.number().safe().nullable(),
  kpiType: z.enum(MarketMakerKpiTypeEnumValues).nullable(),
  bidAskSpreadTarget: z.number().positive().safe().nullable(),
  depth50Target: z.number().positive().safe().nullable(),
  depth100Target: z.number().positive().safe().nullable(),
  depth200Target: z.number().positive().safe().nullable(),
  kpis: z.array(KpiResultSchema).nullable(),
})

const V2BaseEngagementSchema = z.object({
  type: z.enum(EngagementTypeEnumValues),
})

// Shared field-groups — each v2 variant spreads in only what's relevant.
// Future engagement types can opt into any subset (or none).

const v2QuantityFields = {
  qtyTokensLoan: z.number().safe(),
  qtyTokensWorkingCapital: z.number().safe(),
  qtyStablesWorkingCapital: z.number().safe(),
}

const v2KpiFields = {
  generalUptimePercentage: z.number().safe().nullable(),
  kpiOrderBookDominanceThreshold: z.number().safe().nullable(),
  kpiType: z.enum(MarketMakerKpiTypeEnumValues).nullable(),
  bidAskSpreadTarget: z.number().positive().safe().nullable(),
  depth50Target: z.number().positive().safe().nullable(),
  depth100Target: z.number().positive().safe().nullable(),
  depth200Target: z.number().positive().safe().nullable(),
  kpis: z.array(KpiResultSchema).nullable(),
}

const v2LoanFields = {
  engagementStartDate: z.string().date(),
  loanTokenSupplyPercentage: z.number().safe().positive().finite(),
}

const v2LoanCallTermsFields = {
  loanOptionsStyle: z.enum(OptionsStyleEnumValues),
  loanTenorMonths: z.number().safe().int().nonnegative().finite(),
  loanInterestRatePercentage: z.number().safe().nonnegative().finite(),
  tranchesCount: z.number().safe().int().min(1).max(4),
  tranches: z.array(MmEngagementTrancheSchema).min(1, 'Loan Details are required').max(4),
}

const v2RetainerTermsFields = {
  loanStablecoinQuantity: z.number().safe().int().positive().finite(),
  loanCustody: z.enum(MarketMakerQuoteLoanCustodyEnumValues),
  onboardingFeeUsd: z.number().safe().int().nonnegative().finite(),
  unlimitedExchangeCoverage: z.boolean(),
  unlimitedExchangeCoverageMonthlyFeeUsd: z.number().safe().int().nonnegative().finite(),
  singleExchangeCoverageMonthlyFeeUsd: z.number().safe().int().nonnegative().finite(),
  minContractTermMonths: z.number().safe().int().nonnegative().finite(),
  profitSharePercentage: z.number().safe().nonnegative().finite(),
}

const V2LoanCallOptionEngagementSchema = V2BaseEngagementSchema.extend({
  type: z.literal(EngagementTypeEnum.LoanCallOption),
  ...v2QuantityFields,
  ...v2KpiFields,
  ...v2LoanFields,
  ...v2LoanCallTermsFields,
  tranches: v2LoanCallTermsFields.tranches.refine(
    (tranches) =>
      tranches
        .toSorted((a, b) => a.trancheNumber - b.trancheNumber)
        .every((tranche, index) => tranche.trancheNumber === index + 1),
    { message: 'Tranches numbers must be sequential and start from 1' },
  ),
})

const V2RetainerEngagementSchema = V2BaseEngagementSchema.extend({
  type: z.literal(EngagementTypeEnum.RetainerWorkingCapital),
  ...v2QuantityFields,
  ...v2KpiFields,
  ...v2LoanFields,
  ...v2RetainerTermsFields,
})

// Prop Trading carries no engagement-detail or KPI inputs — the FE submits only
// `type`, `coingeckoId`, and zeroed quantity fields. Other v2 fields stay NULL.
// Quantity fields are pinned to `0` so callers can't smuggle a loan/retainer
// payload through the Prop Trading variant.
const V2PropEngagementSchema = V2BaseEngagementSchema.extend({
  type: z.literal(EngagementTypeEnum.PropTrading),
  qtyTokensLoan: z.literal(0),
  qtyTokensWorkingCapital: z.literal(0),
  qtyStablesWorkingCapital: z.literal(0),
})

// Exchange engagements: liquidity provided on a single CEX/DEX across one or
// more spot trading pairs. Like Prop Trading, no loan/working-capital — zeroed
// quantities are pinned so the variant can't smuggle loan fields. Reuses the
// existing KPI block (`v2KpiFields`).
const V2ExchangeEngagementSchema = V2BaseEngagementSchema.extend({
  type: z.literal(EngagementTypeEnum.Exchange),
  qtyTokensLoan: z.literal(0),
  qtyTokensWorkingCapital: z.literal(0),
  qtyStablesWorkingCapital: z.literal(0),
  exchangeId: z.string().min(1, 'Exchange is required'),
  tradingPairs: z.array(MmEngagementTradingPairSchema).min(1, 'At least one trading pair is required'),
  takerFeeReductionPercentage: z.number().safe().min(0).max(100).nullable(),
  makerRebateBonusPercentage: z.number().safe().min(0).max(100).nullable(),
  loanToValueCollateralPercentage: z.number().safe().min(0).max(100),
  engagementStartDate: z.string().date(),
  ...v2KpiFields,
})

type V2EngagementInput =
  | z.infer<typeof V2LoanCallOptionEngagementSchema>
  | z.infer<typeof V2RetainerEngagementSchema>
  | z.infer<typeof V2PropEngagementSchema>
  | z.infer<typeof V2ExchangeEngagementSchema>

// `engagementStartDate` is set-once for native v2 rows. Loan + Retainer keep
// it optional on update so legacy v1 rows (created before the field existed)
// can be converted to v2 safely — the service preserves the persisted value
// once one exists. Exchange has no legacy rows, so it just keeps the field
// required.
const V2UpdateEngagementSchema = z.discriminatedUnion('type', [
  V2LoanCallOptionEngagementSchema.omit({ engagementStartDate: true }).extend({
    engagementStartDate: z.string().date().optional(),
  }),
  V2RetainerEngagementSchema.omit({ engagementStartDate: true }).extend({
    engagementStartDate: z.string().date().optional(),
  }),
  V2PropEngagementSchema,
  V2ExchangeEngagementSchema,
])

const FullEngagementResponseSchema = BaseEngagementSchema.extend({
  // Responses can include Prop Trading engagements (created via the v2 endpoint),
  // so widen `type` here even though the v1 request stays narrow.
  type: z.enum(EngagementTypeEnumValues),
  engagementStartDate: z.string().date().nullable(),
  loanTokenSupplyPercentage: z.number().safe().positive().finite().nullable(),
  loanOptionsStyle: z.enum(OptionsStyleEnumValues).nullable(),
  loanTenorMonths: z.number().safe().int().nonnegative().finite().nullable(),
  loanInterestRatePercentage: z.number().safe().nonnegative().finite().nullable(),
  tranchesCount: z.number().safe().int().min(1).max(4).nullable(),
  tranches: z.array(MmEngagementTrancheSchema).nullable(),
  loanStablecoinQuantity: z.number().safe().int().positive().finite().nullable(),
  loanCustody: z.enum(MarketMakerQuoteLoanCustodyEnumValues).nullable(),
  onboardingFeeUsd: z.number().safe().int().nonnegative().finite().nullable(),
  unlimitedExchangeCoverage: z.boolean().nullable(),
  unlimitedExchangeCoverageMonthlyFeeUsd: z.number().safe().int().nonnegative().finite().nullable(),
  singleExchangeCoverageMonthlyFeeUsd: z.number().safe().int().nonnegative().finite().nullable(),
  minContractTermMonths: z.number().safe().int().nonnegative().finite().nullable(),
  profitSharePercentage: z.number().safe().nonnegative().finite().nullable(),
  exchangeId: z.string().nullable(),
  takerFeeReductionPercentage: z.number().safe().nullable(),
  makerRebateBonusPercentage: z.number().safe().nullable(),
  loanToValueCollateralPercentage: z.number().safe().nullable(),
  tradingPairs: z.array(MmEngagementTradingPairSchema).nullable(),
})

const AcceptedRfqTermsBaseSchema = z.object({
  generalUptimePercentage: v2KpiFields.generalUptimePercentage,
  kpiOrderBookDominanceThreshold: v2KpiFields.kpiOrderBookDominanceThreshold,
  kpiType: z.literal(MarketMakerKpiTypeEnum.Advanced),
  kpis: z.array(KpiResultSchema),
  loanTokenSupplyPercentage: v2LoanFields.loanTokenSupplyPercentage,
})

const AcceptedRfqLoanCallOptionTermsSchema = AcceptedRfqTermsBaseSchema.extend({
  type: z.literal(EngagementOptionEnum.LoanCallOption),
  ...v2LoanCallTermsFields,
})

const AcceptedRfqRetainerTermsSchema = AcceptedRfqTermsBaseSchema.extend({
  type: z.literal(EngagementOptionEnum.RetainerWorkingCapital),
  ...v2RetainerTermsFields,
})

export const AcceptedRfqTermsSchema = z
  .discriminatedUnion('type', [AcceptedRfqLoanCallOptionTermsSchema, AcceptedRfqRetainerTermsSchema])
  .superRefine((terms, ctx) => {
    if (terms.type === EngagementOptionEnum.LoanCallOption && terms.tranches.length !== terms.tranchesCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Expected ${terms.tranchesCount} tranche(s), got ${terms.tranches.length}`,
        path: ['tranches'],
      })
    }
  })

export type AcceptedRfqTerms = z.infer<typeof AcceptedRfqTermsSchema>

export const GetMmEngagementResponseBodySchema = FullEngagementResponseSchema.extend({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  coingeckoId: z.string().nullable(),
  status: z.enum(MarketMakerEngagementsStatusEnumValues),
  acceptedRfqTerms: AcceptedRfqTermsSchema.nullable(),
  summary: EngagementSummarySchema.pick({
    firstReportReceivedAt: true,
    lastReportReceivedAt: true,
    totalReportsCount: true,
  }),
})

interface BasicKpiInput {
  kpiType: MarketMakerKpiTypeEnumType | null
  bidAskSpreadTarget: number | null
  depth50Target: number | null
  depth100Target: number | null
  depth200Target: number | null
}

function validateBasicKpis(data: BasicKpiInput, ctx: z.RefinementCtx) {
  if (data.kpiType !== MarketMakerKpiTypeEnum.Basic) {
    return
  }
  if (!data.bidAskSpreadTarget) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'number',
      received: typeof data.bidAskSpreadTarget,
      message: 'Bid ask spread target is required',
      path: ['bidAskSpreadTarget'],
    })
  }
  if (!data.depth50Target) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'number',
      received: typeof data.depth50Target,
      message: 'Depth 50 target is required',
      path: ['depth50Target'],
    })
  }
  if (!data.depth100Target) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'number',
      received: typeof data.depth100Target,
      message: 'Depth 100 target is required',
      path: ['depth100Target'],
    })
  }
  if (!data.depth200Target) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'number',
      received: typeof data.depth200Target,
      message: 'Depth 200 target is required',
      path: ['depth200Target'],
    })
  }
}

function validateV2EngagementCreate(data: V2EngagementInput, ctx: z.RefinementCtx) {
  if (data.type === EngagementTypeEnum.PropTrading) {
    return
  }
  validateV2EngagementStartDate(data.engagementStartDate, ctx)
  validateV2EngagementShared(data, ctx)
}

function validateV2EngagementUpdate(data: z.infer<typeof V2UpdateEngagementSchema>, ctx: z.RefinementCtx) {
  if (data.type === EngagementTypeEnum.PropTrading) {
    return
  }
  if (data.engagementStartDate) {
    validateV2EngagementStartDate(data.engagementStartDate, ctx)
  }
  validateV2EngagementShared(data, ctx)
}

function validateV2EngagementStartDate(engagementStartDate: string, ctx: z.RefinementCtx) {
  // 24h grace window so users in timezones ahead of UTC (e.g. UTC+9) aren't
  // falsely rejected when picking "today" locally.
  const supplied = new UTCDate(engagementStartDate)
  const cutoff = addHours(new UTCDate(), FUTURE_DATE_GRACE_HOURS)
  if (isAfter(supplied, cutoff)) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_date,
      message: 'Engagement start date cannot be in the future',
      path: ['engagementStartDate'],
    })
  }
}

// ---------------------------------------------------------------------------
// Exchange trading-pair rules (each helper validates one rule, all run
// together in `validateExchangeEngagement`).
// ---------------------------------------------------------------------------

type TradingPair = z.infer<typeof MmEngagementTradingPairSchema>

/** Key that's identical for (A,B) and (B,A) — used to detect duplicate pairs
 * regardless of base/quote order. */
function unorderedTradingPairKey(pair: TradingPair): string {
  return [pair.baseCoingeckoId, pair.quoteCoingeckoId].sort().join('::')
}

function validateTradingPairBaseQuoteDistinct(pair: TradingPair, idx: number, ctx: z.RefinementCtx) {
  if (pair.baseCoingeckoId === pair.quoteCoingeckoId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Base and quote must be different tokens',
      path: ['tradingPairs', idx],
    })
  }
}

function validateTradingPairsUnique(pairs: TradingPair[], ctx: z.RefinementCtx) {
  const seen = new Set<string>()
  pairs.forEach((pair, idx) => {
    const key = unorderedTradingPairKey(pair)
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Each trading pair must be unique',
        path: ['tradingPairs', idx],
      })
    }
    seen.add(key)
  })
}

function validateTradingPairNumbersSequential(pairs: TradingPair[], ctx: z.RefinementCtx) {
  const expected = new Set(Array.from({ length: pairs.length }, (_, i) => i + 1))
  const actual = new Set(pairs.map((p) => p.pairNumber))
  const isSequential = actual.size === expected.size && [...actual].every((n) => expected.has(n))
  if (!isSequential) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Pair numbers must be sequential and start from 1',
      path: ['tradingPairs'],
    })
  }
}

/** Project-anchor rule: Pair 1's base must equal the engagement's top-level
 * coingeckoId. Caller passes `coingeckoId` because it lives at a different
 * level of the schema (the create-body extension) than the trading pairs. */
function validateTradingPair1AnchorsProject(pairs: TradingPair[], coingeckoId: string, ctx: z.RefinementCtx) {
  const pair1 = pairs.find((p) => p.pairNumber === 1)
  if (!pair1 || pair1.baseCoingeckoId === coingeckoId) {
    return
  }
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Pair 1's Base Asset must match the engagement's project token",
    path: ['tradingPairs', pairs.indexOf(pair1), 'baseCoingeckoId'],
  })
}

function validateExchangeEngagement(
  data: Extract<
    V2EngagementInput | z.infer<typeof V2UpdateEngagementSchema>,
    { type: typeof EngagementTypeEnum.Exchange }
  >,
  ctx: z.RefinementCtx,
) {
  data.tradingPairs.forEach((pair, idx) => validateTradingPairBaseQuoteDistinct(pair, idx, ctx))
  validateTradingPairsUnique(data.tradingPairs, ctx)
  validateTradingPairNumbersSequential(data.tradingPairs, ctx)
  validateBasicKpis(data, ctx)
}

function validateV2EngagementShared(
  data: V2EngagementInput | z.infer<typeof V2UpdateEngagementSchema>,
  ctx: z.RefinementCtx,
) {
  if (data.type === EngagementTypeEnum.PropTrading) {
    return
  }
  if (data.type === EngagementTypeEnum.Exchange) {
    validateExchangeEngagement(data, ctx)
    return
  }
  validateBasicKpis(data, ctx)

  // The FE derives `qty*` from `loanTokenSupplyPercentage × mts`, but `mts` can
  // be 0 when CoinGecko has no supply data for the coin — leaving the derived
  // qty at 0 even with a positive %. Mirror v1's positivity guard so a zero-qty
  // engagement can't slip into monitoring/leaderboard calculations.
  const tokensQty = data.type === EngagementTypeEnum.LoanCallOption ? data.qtyTokensLoan : data.qtyTokensWorkingCapital
  if (tokensQty <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'number',
      inclusive: true,
      message: 'Quantity of tokens must be greater than 0',
      path: [data.type === EngagementTypeEnum.LoanCallOption ? 'qtyTokensLoan' : 'qtyTokensWorkingCapital'],
    })
  }

  if (data.type === EngagementTypeEnum.LoanCallOption) {
    if (data.tranches.length !== data.tranchesCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Expected ${data.tranchesCount} tranche(s), got ${data.tranches.length}`,
        path: ['tranches'],
      })
    }
    const sum = data.tranches.reduce((acc, t) => acc + (t.tokenSupplyPercentage || 0), 0)
    const precision = 3
    const factor = 10 ** precision
    const difference = Math.abs(Math.round((data.loanTokenSupplyPercentage - sum) * factor) / factor)

    if (isFloatLessWithPrecision(data.loanTokenSupplyPercentage, sum, precision)) {
      const msg = `The % of token supply allocated to your tranches has been exceeded by ${difference}%. The SUM of your tranches cannot exceed ${formatValue(data.loanTokenSupplyPercentage, 'percent:3')} of Token Supply.`
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg, path: ['loanTokenSupplyPercentage'] })
      data.tranches.forEach((_, index) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: msg,
          path: ['tranches', index, 'tokenSupplyPercentage'],
        })
      })
    } else if (isFloatGreaterWithPrecision(data.loanTokenSupplyPercentage, sum, precision)) {
      const msg = `There is still ${difference}% of the total token supply % pending to be allocated on your tranches. Please adjust the values to continue.`
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg, path: ['loanTokenSupplyPercentage'] })
      data.tranches.forEach((_, index) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: msg,
          path: ['tranches', index, 'tokenSupplyPercentage'],
        })
      })
    }
  } else {
    if (data.unlimitedExchangeCoverage) {
      if (data.unlimitedExchangeCoverageMonthlyFeeUsd <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Monthly Fee must be greater than 0',
          path: ['unlimitedExchangeCoverageMonthlyFeeUsd'],
        })
      }
    } else if (data.singleExchangeCoverageMonthlyFeeUsd <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Monthly Fee must be greater than 0',
        path: ['singleExchangeCoverageMonthlyFeeUsd'],
      })
    }
    if (data.loanStablecoinQuantity <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Stablecoins quantity must be greater than 0',
        path: ['loanStablecoinQuantity'],
      })
    }
  }
}

const coingeckoIdField = { coingeckoId: z.string().min(1, 'Coingecko ID is required') }

export const CreateMmEngagementV2RequestBodySchema = z
  .discriminatedUnion('type', [
    V2LoanCallOptionEngagementSchema.extend(coingeckoIdField),
    V2RetainerEngagementSchema.extend(coingeckoIdField),
    V2PropEngagementSchema.extend(coingeckoIdField),
    V2ExchangeEngagementSchema.extend(coingeckoIdField),
  ])
  .superRefine(validateV2EngagementCreate)
  // The Pair 1-anchors-project rule lives at the create-body level (not in
  // `validateExchangeEngagement`) because only here does the schema type
  // expose the top-level `coingeckoId`.
  .superRefine((data, ctx) => {
    if (data.type === EngagementTypeEnum.Exchange) {
      validateTradingPair1AnchorsProject(data.tradingPairs, data.coingeckoId, ctx)
    }
  })

export const UpdateMmEngagementV2RequestBodySchema = V2UpdateEngagementSchema.superRefine(validateV2EngagementUpdate)
